/**
 * About this Sanitisation:
 * https://desygner.atlassian.net/wiki/spaces/EDIT/pages/2520842353/Clip+Path+Sanitise
 */

import { logError } from '../logger';

export function getClipPaths (svgDocument) {
  return [...svgDocument.querySelectorAll('clipPath')];
}

export function getElementsWithClipPath (svgDocument) {
  return [...svgDocument.querySelectorAll('[clip-path]')];
}

export function findElementsWithClipPath (svgDocument, clipPathId) {
  return [...svgDocument.querySelectorAll(`[clip-path="url(#${clipPathId})"]`)];
}

export function getDuplicatedClipPath (svgDocument) {
  const clipPathElements = [...svgDocument.querySelectorAll('clipPath')];
  const clipPathIds = clipPathElements.map(element => element.id);
  const duplicatedClipPathIds = clipPathIds.filter(id => (findElementsWithClipPath(svgDocument, id).length > 1));

  return clipPathElements.filter(element => duplicatedClipPathIds.includes(element.id));
}

export function generateNewClipPath (clipPath, newId) {
  const newClipPathElement = clipPath.cloneNode(true);
  newClipPathElement.id = newId;

  clipPath.parentNode.appendChild(newClipPathElement);

  return newClipPathElement;
}

export function deleteClipPath (clipPath) {
  clipPath.parentNode.removeChild(clipPath);
}

/**
 * The first element will keep the original clipPath
 * The rest of the elements will have a new clipPath
 */
export function createUniqueClipPath (clipPath, elements) {
  elements.slice(1).forEach(element => {
    const newClipPath = generateNewClipPath(clipPath, `${clipPath.id}-${element.id}`);
    newClipPath.querySelectorAll('[id]').forEach(childElement => {
      childElement.id = `${childElement.id}-${element.id}`;
    });
    element.setAttribute('clip-path', `url(#${newClipPath.id})`);
  });
}

/**
 * Remove clipPath if it is not used by any element
 * Create a new clipPath if it is used by more than one element
 */
export function clipPathSanitize (svgDocument) {
  const clipPaths = getClipPaths(svgDocument);

  clipPaths.forEach(clipPath => {
    const elements = findElementsWithClipPath(svgDocument, clipPath.id);
    if (elements.length === 0) {
      deleteClipPath(clipPath);
    } else if (elements.length > 1) {
      createUniqueClipPath(clipPath, elements);
    }
  });
}

export function removeAttributeWithInvalidClipPath (svgDocument) {
  const elements = getElementsWithClipPath(svgDocument);

  elements.forEach(element => {
    const clipPathId = element.getAttribute('clip-path').replace('url(#', '').replace(')', '');
    if (!svgDocument.getElementById(clipPathId)) {
      element.removeAttribute('clip-path');
    }
  });
}

export function fixBrokenElements (svgDocument) {
  const brokenElements = [...svgDocument.querySelectorAll('.element > .element.logo', '.element > .element.sticker')];

  brokenElements.forEach(element => {
    const fragment = new DocumentFragment();
    const parent = element.parentNode;

    element.classList.remove('subelement');

    fragment.appendChild(element);

    [...fragment.children].forEach(child => {
      parent.insertAdjacentElement('afterend', child);
    });

    parent.remove();
  });
}

/**
 * It moves all the clipPaths from the following def to the first one
 */
export function sanitizeDefs (svgDocument) {
  const defs = [...svgDocument.querySelectorAll('svg defs')];
  defs.slice(1).forEach((def) => {
    [...def.childNodes].forEach((clipPath) => defs[0].appendChild(clipPath));
    def.remove();
  });
}

/**
 * Remove clipPath if it is not used by any element
 * Create a new clipPath if it is used by more than one element
 * Remove clipPath attribute if it is not valid
 */
export function closeClipperLayers (svgDocument) {
  const clipperImageLayer = svgDocument.querySelector('.clipperImage');
  const clipperSelectorLayer = svgDocument.querySelector('.clipperSelector');
  const layoutOverlay = svgDocument.querySelector('#layoutOverlay');

  if (layoutOverlay) layoutOverlay.remove();

  if (!clipperImageLayer) return;

  const layoutClipperImage = clipperImageLayer.querySelector('.layoutClipperImage');

  if (layoutClipperImage) {
    const originalId = layoutClipperImage.getAttribute('data-old-id');
    const originalElement = originalId && svgDocument.querySelector('#' + originalId);

    if (originalElement) {
      const positionAttributes = ['x', 'y', 'width', 'height'];
      positionAttributes.forEach(attribute => {
        const value = layoutClipperImage.getAttribute(attribute);
        if (value !== null) {
          originalElement.setAttribute(attribute, value);
        }
      });
    }
  }

  // Images in background/main layers were hidden during reposition mode
  svgDocument.querySelectorAll('.background image, .main image').forEach(image => {
    const style = image.getAttribute('style');
    if (style) {
      image.setAttribute('style', style.replace(/display\s*:\s*none\s*;?/g, '').trim());
      if (!image.getAttribute('style')) {
        image.removeAttribute('style');
      }
    }
  });

  clipperImageLayer.remove();
  if (clipperSelectorLayer) clipperSelectorLayer.remove();
}

export function clipPathCleanup (svgString) {
  try {
    const svgDocument = svgedit.utilities.text2xml(svgString, true);

    removeAttributeWithInvalidClipPath(svgDocument);
    clipPathSanitize(svgDocument);
    fixBrokenElements(svgDocument);
    sanitizeDefs(svgDocument);
    closeClipperLayers(svgDocument);

    return INKIVE.Helpers.serializeToString(svgDocument);
  } catch (error) {
    logError('clipPathCleanup')(error);

    return svgString;
  }
}
